import { useState } from 'react';

import { notifications } from '@mantine/notifications';
import { useSetRecoilState } from 'recoil';
import useSWR from 'swr';

import { getUser } from '@/features/auth/api/getUser';
import { useAuthorizeRouter } from '@/features/auth/hooks/useAuthorizeRouter';
import { ProcessStatus, ResourceStatus, ResourceType } from '@/features/polling/hooks/usePolling';
import { getSite } from '@/features/saveSite/api/getSite';
import { getProcessingShapesReports } from '@/features/schedule/api/getProcessingShapesReports';
import { OutputErrorLog } from '@/lib/error';
import { prevResourceStatusDataState } from '@/store/atom';
import { SWR_KEYS } from '@/utils/constant';
import { getNotificationDetail } from '@/utils/getNotificationDetail';

export const useCreateProcessingShapeReport = () => {
  const router = useAuthorizeRouter();
  const { siteId } = router.query;
  const setPrevResourceStatusData = useSetRecoilState(prevResourceStatusDataState);

  const { data: site } = useSWR([SWR_KEYS.SITE, siteId], () =>
    siteId ? getSite({ siteId }) : null
  );
  const { data: user } = useSWR(SWR_KEYS.USER, () => getUser());

  const [isLoading, setIsLoading] = useState(false);

  const createProcessingShapeReport = async () => {
    try {
      setIsLoading(true);

      if (!user || !site) {
        throw new Error('user or site is undefined');
      }

      const result = await getProcessingShapesReports({ siteId: site.id, isPublic: site.isOpen });
      if (!result) throw new Error('getProcessingShapesReports error');

      // prevResourceStatusDataにIN_PROGRESS状態を追加
      // これにより、次のポーリングでDONEになった場合に検出できる
      // CreateStatus.IN_PROGRESS = 2
      // 出来高帳票は他の非同期処理と比べて一瞬で処理が完了する場合がある。その際にusePollingにDL処理を任せてしまうと、うまくいかない。なので帳票作成開始されたら即時prevResourceStatusDataに追加する。
      if (result.id && result.createStatusId === 2) {
        const newStatus: ResourceStatus = {
          resourceId: result.id,
          resourceType: ResourceType.ProcessingShape,
          processStatus: ProcessStatus.IN_PROGRESS,
        };

        try {
          setPrevResourceStatusData((prev) => {
            // prevがundefinedやnullの場合の処理を追加
            const prevArray = prev || [];

            // 既に存在する場合は更新、存在しない場合は追加
            const existingIndex = prevArray.findIndex(
              (item) =>
                item.resourceId === result.id && item.resourceType === ResourceType.ProcessingShape
            );

            if (existingIndex >= 0) {
              const updated = [...prevArray];
              updated[existingIndex] = newStatus;
              return updated;
            }
            return [...prevArray, newStatus];
          });

          // 進行中のtoasterをすぐに表示
          const notificationDetail = getNotificationDetail(newStatus, {
            resourceName: '進捗詳細レポート',
            dataFiles: [],
          });
          if (notificationDetail.title) {
            notifications.show({
              id: newStatus.resourceId + newStatus.resourceType,
              title: notificationDetail.title,
              message: notificationDetail.message,
              color: notificationDetail.color,
              loading: notificationDetail.loading,
              autoClose: notificationDetail.autoClose,
            });
          }
        } catch (e) {
          OutputErrorLog('setPrevResourceStatusData error', e);
          // エラーが発生しても処理は続行する
        }
      }
    } catch (e) {
      OutputErrorLog('createProcessingShapeReport', e);
      notifications.show({
        title: 'エラー',
        message: '進捗詳細レポートの作成に失敗しました',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createProcessingShapeReport,
    isLoading,
  };
};
